import { GridBasicGroupNode, gridClasses, GridColDef, GridColumnMenu, GridColumnMenuProps, GridEventListener, GridRenderCellParams, useGridApiRef, useKeepGroupedColumnsHidden } from "@mui/x-data-grid-premium";
import DataGrid from "components/DataGrid/DataGrid";
import SendIcon from '@mui/icons-material/Send';
import SectionTitle from "components/UI/SectionTitle";
import { PageSection } from "components/UI/Structure/PageSection";
import StyledCard from "components/UI/StyledCard";
import React, { SetStateAction, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { ClientRecord, ContractRecord } from "lib/DataModel";
import { getIn } from "components/Formik/forms";
import ClientCampaignsResource from "resources/ClientCampaignsResource";
import ContractsResource from "resources/ContractsResource";
import IconButton from "@mui/material/IconButton";
import StandardDialog from "components/UI/Dialog/StandardDialog";
import Box from "@mui/system/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { groupBy, sumBy } from "lodash-es";
import { useMutation, useSuspenseQuery } from "@apollo/client";
import { useNotifications } from "lib/NotificationsProvider";
import COLORS from 'lib/colors';
import Suspenseful from "components/Suspenseful";
import moment, { Moment } from "moment-timezone";
import { useTimezone } from "lib/TimezoneProvider";
import { useBookends } from "lib/BookendsProvider";
import { useOrbit } from "providers/OrbitProvider";
import { useCurrentUser } from "lib/CurrentUserProvider";
import { toDollars } from "lib/utils";
import { CREATE_INVOICE_MUTATION, GET_INVOICES_QUERY, RESEND_INVOICE_MUTATION } from "./queries";
import { InvoiceFieldsFragment } from "generated/graphql";
import Tooltip from "@mui/material/Tooltip";
import LockSendIcon from "components/UI/icons/LockSendIcon";

type GridColDefWithColumnOrder = GridColDef & {
  columnOrder: number
}

const COMMON_INVOICE_COLUMNS : GridColDefWithColumnOrder[] = [
  {
    headerName: "Vendor Name",
    field: "vendorName",
    type: "string",
    minWidth: 200,
    columnOrder: 2,
  },
  {
    headerName: "Billable Leads",
    field: "billableLeadCount",
    type: "number",
    flex: 1,
    columnOrder: 5,
    disableColumnMenu: true,
  },
  {
    headerName: "CPL",
    field: "cplInCents",
    valueGetter: (_value, row) => {
      if (!row.billableLeadCount || !row.totalCostInCents) {
        return null;
      }
      return row.totalCostInCents / row.billableLeadCount
    },
    type: "number",
    valueFormatter: (value) => value ? toDollars(Number(value)) : '—',
    flex: 1,
    columnOrder: 6,
    disableColumnMenu: true,
  },
  {
    headerName: "Total",
    field: "totalCostInCents",
    type: "number",
    valueFormatter: (value) => toDollars(Number(value)),
    flex: 1,
    columnOrder: 7,
    disableColumnMenu: true,
  },
]

const CLIENT_CAMPAIGN_COLUMN = {
  headerName: "Campaign Name",
  field: "clientCampaignName",
  type: "string",
  minWidth: 200,
  columnOrder: 3,
} as const

const INVOICED_PERIOD_COLUMN = {
  headerName: "Invoiced Period",
  field: "invoicedPeriod",
  type: "string",
  minWidth: 200,
  columnOrder: 1,
} as const

interface InvoiceRequestContentProps {
  invoice: Invoice | InvoiceData
  invoiceMoment: Moment
}

function InvoiceRequestContent({invoice, invoiceMoment}: InvoiceRequestContentProps) {
  const { store } = useOrbit()
  const { contract, invoiceLineItems } = invoice
  const vendor = useMemo(() => store.cache.query(q => q.findRelatedRecord(contract, 'vendor')), [contract, store])
  const contactName = getIn(contract, 'attributes.vendorContactEmail') ? getIn(contract, 'attributes.vendorContactName') : getIn(vendor, 'attributes.contactName')
  const contactEmail = getIn(contract, 'attributes.vendorContactEmail') ? getIn(contract, 'attributes.vendorContactEmail') : getIn(vendor, 'attributes.contactEmail')

  const firstName = contactName && contactName.split(' ')[0]
  const totalLeadCount = sumBy(invoiceLineItems, 'billableLeadCount')
  const totalCostInCents = sumBy(invoiceLineItems, 'subtotalInCents')

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
        <Typography variant="h4">
          Email Preview
        </Typography>
        {contactEmail &&
          <Typography
            variant="body2"
            sx={{ pl: 2, color: "info.main" }}
          >

            Vendor Recipient: {contactName && ` ${contactName}, `}{contactEmail}
          </Typography>
        }
      </Box>
      <Box
        sx={{
          mt: 1,
          p: '12px 18px 18px 18px',
          borderRadius: '5px',
          border: `solid 1px ${COLORS.cadetGray}`,
          minHeight: 270,
          '& > p': {m: 0, mb: 1}
        }}
      >
        <p>Hello{firstName && ` ${firstName}`},</p>
        <p>Please see the {invoiceMoment.format('MMMM YYYY')} billable lead count and campaign costs for invoice submission.</p>

        <Table sx={{ maxWidth: 700, mt: 3, '.MuiTableCell-root': { p: 0, py: 0.5, border: 'none' } }} aria-label="simple table">
          <TableHead sx={{ '.MuiTableCell-root': { fontWeight: 'bold' } }}>
            <TableRow>
              <TableCell>Campaign Name</TableCell>
              <TableCell>Campaign ID</TableCell>
              <TableCell align="right">Billable Leads</TableCell>
              <TableCell align="right">Cost Per</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoiceLineItems.map((row) => (
              <TableRow
                key={row.clientCampaignName}

              >
                <TableCell component="th" scope="row">
                  {row.clientCampaignName}
                </TableCell>
                <TableCell>{`CAMP-${row.publicId}`}</TableCell>
                <TableCell align="right">{row.billableLeadCount}</TableCell>
                <TableCell align="right">{toDollars((row.subtotalInCents/row.billableLeadCount))}</TableCell>
                <TableCell align="right">{toDollars(row.subtotalInCents)}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ '.MuiTableCell-root': { fontWeight: 'bold', pt: 1 }, borderTop: `1px solid ${COLORS.charcoal}` }}>
              <TableCell component="th" scope="row">
                Total
              </TableCell>
              <TableCell align="right"></TableCell>
              <TableCell align="right">{totalLeadCount}</TableCell>
              <TableCell align="right">{'—'}</TableCell>
              <TableCell align="right">{totalCostInCents ? toDollars(totalCostInCents) : '—'}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </>
  )
}

function statusRenderer(variant : InvoiceTableVariant) : (params: GridRenderCellParams) => React.ReactNode {
  switch (variant) {
    case 'draft':
      return ({ rowNode }) => rowNode.type === 'group' ? 'Draft' : null
    case 'billable':
      return ({ rowNode }) => rowNode.type === 'group' ? 'Active' : null
    default:
      return ({ value }) => value
  }
}

function SendButton({ clickHandler, rowNode, label, Icon }) {
  return (
    <Tooltip title={label}>
      <IconButton className='actionButton' onClick={(event)=>{ event.stopPropagation(); clickHandler(event, rowNode) }} aria-label={label} size="small" sx={{color: COLORS.charcoal, padding: .5 }}>
        <Icon />
      </IconButton>
    </Tooltip>
  )
}

function CustomColumnMenu(props: GridColumnMenuProps) {

  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuSortItem: null,
        columnMenuColumnsItem: null,
        columnMenuPinningItem: null,
        columnMenuAggregationItem: null,
        columnMenuGroupingItem: null,
      }}
    />
  );
}

type InvoiceTableVariant = 'draft' | 'billable' | 'history'

interface InvoiceTableProps {
  data: RowData[] | InvoiceData[] | null
  groupingField: string
  leafField: string
  customColumns: GridColDefWithColumnOrder[]
  variant: InvoiceTableVariant
  controlRenderCell: (params: GridRenderCellParams) => React.ReactNode
  onLeafRowClick?: (e: unknown, data: unknown) => void
}

function InvoiceTable({ data, groupingField, leafField, customColumns = [], variant, controlRenderCell, onLeafRowClick = (_e, _data) => {return} }: InvoiceTableProps) {
  const apiRef = useGridApiRef()
  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: {
        model: [groupingField]
      },
      columns: {
        columnVisibilityModel: {
          // Hide the column used for leaves
        [leafField]: false,
        },
      },
      aggregation: {
        model: {
          billableLeadCount: 'sum',
          totalCostInCents: 'sum',
        },
      },
      pinnedColumns: { right: ['id'] },
    },
  });

  const columns : GridColDefWithColumnOrder[] = [
    ...COMMON_INVOICE_COLUMNS,
    ...customColumns,
    {
      headerName: "Status",
      field: "status",
      type: "string",
      flex: 1,
      columnOrder: 4,
      renderCell: statusRenderer(variant),
      disableColumnMenu: true,
    } as const,
    {
      field: 'id',
      headerName: '',
      type: 'string',
      renderCell: controlRenderCell,
      width: 52,
      columnOrder: 8,
      disableColumnMenu: true,
      sortable: false,
      filterable: false,
      pinnable: false,
      headerClassName: 'menu-col',
      cellClassName: 'menu-col',
    } as const,
  ].sort((a, b) => a.columnOrder - b.columnOrder)

  const onRowClick = React.useCallback<GridEventListener<'rowClick'>>(
    ({ id }) => {
      const rowNode = apiRef.current?.getRowNode(id);
      if (rowNode && rowNode.type === 'group') {
        apiRef.current?.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
      }
      if (rowNode?.type === 'leaf') {
        onLeafRowClick(null, rowNode)
      }
    },
    [apiRef, onLeafRowClick],
  );

  return (
    <DataGrid
      columns={columns}
      rows={data || []}
      apiRef={apiRef}
      initialState={initialState}
      onRowClick={onRowClick}
      groupingColDef={{ minWidth: 400, leafField: leafField }}
      slots={{
        columnMenu: CustomColumnMenu,
      }}
      sx={{
        '.MuiDataGrid-footerContainer': {display: 'none'},
        "& .actionButton": {
          opacity: 0
        },
        [`& .${gridClasses.row}:hover`]: {
          ".actionButton": {
            opacity: 1
          }
        },
        '& .MuiDataGrid-columnHeader.menu-col, & .MuiDataGrid-cell.menu-col': {
          paddingLeft: '10px',
          paddingRight: '10px',
        },
      }}
    />
  )
}

function incrementIndex(index: number, arrayLength: number): number {
  return (index + 1) % arrayLength
}

function decrementIndex(index: number, arrayLength: number): number {
  return (index - 1 + arrayLength) % arrayLength
}

function findInvoiceByLineItemId(invoices: Invoice[], lineItemId: string): Invoice | undefined {
  return invoices.find(invoice =>
    invoice.invoiceLineItems.some(item => item.id === lineItemId)
  );
}

type InvoiceLineItem = {
  // this is the Allocation ID in the draft case, and the InvoiceLineItem ID in any other case
  id: string
  billableLeadCount: number
  subtotalInCents: number
  clientCampaignName: string
  publicId: string
  position: number
}

type Invoice = {
  id?: string
  contractId: string
  vendorName: string
  invoiceMonth: number
  invoiceYear: number
  status: string
  billableLeadCount: number
  totalCostInCents: number
  invoiceLineItems: InvoiceLineItem[]
  contract: ContractRecord
}

type BaseDialogState = {
  open: boolean
  invoices: Invoice[]
}

type ClosedDialogState = BaseDialogState & {
  open: false
}

type OpenSingleDialogState = BaseDialogState & {
  open: true
  selectedContractId: string | undefined
}

type OpenMultipleDialogState = BaseDialogState & {
  open: true
  currentIndex: number
}

function isOpenMultipleDialogState(state: DialogState) : state is OpenMultipleDialogState {
  return 'currentIndex' in state
}

function isOpenSingleDialogState(state: DialogState) : state is OpenSingleDialogState {
  return 'selectedContractId' in state
}

type DialogState = ClosedDialogState | OpenSingleDialogState | OpenMultipleDialogState

type DialogAction = {
  type: 'SEND_ALL_INVOICES' | 'CLOSE_DIALOG' | 'INCREMENT_INDEX' | 'DECREMENT_INDEX'
} | {
  type: 'SEND_INVOICE'
  allocationId: string
} | {
  type: 'UPDATE_INVOICES'
  invoices: Invoice[]
}

function dialogStateReducer(state : DialogState, action : DialogAction) : DialogState {
  switch (action.type) {
    case 'SEND_INVOICE':
      {
        const invoice = findInvoiceByLineItemId(state.invoices, action.allocationId)
        if(!invoice) {
          throw new Error(`No such invoice: ${action.allocationId}`)
        }

        return {
          open: true,
          invoices: state.invoices,
          selectedContractId: invoice.contractId
        }
      }

    case 'SEND_ALL_INVOICES':
      return {
        open: true,
        invoices: state.invoices,
        currentIndex: 0
      }

    case 'INCREMENT_INDEX':
      if(!isOpenMultipleDialogState(state)) {
        throw new Error(`Invalid action ${action.type} for state ${JSON.stringify(state)}`)
      }
      return {
        open: true,
        invoices: state.invoices,
        currentIndex: incrementIndex(state.currentIndex || 0, state.invoices.length)
      }

    case 'DECREMENT_INDEX':
      if(!isOpenMultipleDialogState(state)) {
        throw new Error(`Invalid action ${action.type} for state ${JSON.stringify(state)}`)
      }
      return {
        open: true,
        invoices: state.invoices,
        currentIndex: decrementIndex(state.currentIndex || 0, state.invoices.length)
      }

    case 'CLOSE_DIALOG':
      return {
        open: false,
        invoices: state.invoices,
      }

    case 'UPDATE_INVOICES':
      return {
        ...state,
        invoices: action.invoices
      }

    default:
      throw Error('Unknown action: ' + (action as DialogAction).type);
  }
}

type SendDialogProps = {
  invoice: Invoice
  invoiceMoment: Moment
  onComplete: () => void
  handleClose: () => void
  onClickPrev: (() => void) | undefined
  onClickNext: (() => void) | undefined
}

function SendDialog({ invoice, invoiceMoment, onComplete, handleClose, onClickPrev, onClickNext } : SendDialogProps) {
  const [submitting, setSubmitting] = useState(false)

  const { addNotification } = useNotifications()
  const [gqlSend] = useMutation(CREATE_INVOICE_MUTATION)
  const createInvoice = useCallback(({sendEmail}) => {
    setSubmitting(true)

    const mutation = {
      sendEmail: sendEmail,
      month: invoice.invoiceMonth,
      year: invoice.invoiceYear,
      contractId: invoice.contractId,
      createInvoiceLineItems: invoice.invoiceLineItems.map(row => {
        return {
          position: row.position,
          clientCampaignName: row.clientCampaignName,
          clientCampaignExportableId: `CAMP-${row.publicId}`,
          clientCampaignId: row.id,
          subtotalInCents: row.subtotalInCents,
          billableLeadCount: row.billableLeadCount,
        }
      }),
    }
    gqlSend({ variables: mutation }).then(() => {
      if(sendEmail) {
        addNotification({variant: 'notice', message: `Invoice Request sent to ${invoice.vendorName}`})
      } else {
        addNotification({variant: 'notice', message: `Billable Statuses locked for ${invoice.vendorName}`})
      }
    }).catch(_error => {
      if(sendEmail) {
        addNotification({variant: 'alert', message: "Trouble sending Invoice Request. Please try again later."})
      } else {
        addNotification({variant: 'alert', message: "Trouble locking billable status. Please try again later."})
      }
    }).finally(() => {
      onComplete()
      setSubmitting(false)
    })
  }, [invoice.invoiceMonth, invoice.invoiceYear, invoice.contractId, invoice.invoiceLineItems, invoice.vendorName, gqlSend, addNotification, onComplete])

  return (
    <StandardDialog
      title={`${invoiceMoment.format('MMMM YYYY')} Invoice Request`}
      description={<>Send the following invoice request for <b>{invoiceMoment.format('MMMM YYYY')}</b> to <b>{invoice.vendorName}</b>?</>}
      onCancel={handleClose}
      onSecondaryConfirm={() => createInvoice({sendEmail: false})}
      secondaryConfirmText='Lock Billable Status'
      onConfirm={() => createInvoice({sendEmail: true})}
      confirmText='Lock & Send Invoice'
      maxWidth='840px'
      loading={submitting}
      onClickPrev={onClickPrev}
      onClickNext={onClickNext}
    >
      <InvoiceRequestContent invoice={invoice} invoiceMoment={invoiceMoment} />
    </StandardDialog>
  )
}

function invoicesFromData(data: RowData[], invoicePeriod: Moment): Invoice[] {
  if (!data) {
    return []
  }

  const dataByContract = groupBy(data, 'contractId')
  const contractsToBeInvoiced = Object.keys(dataByContract)
  const invoices = contractsToBeInvoiced.map(contractId => {
    const contractData = dataByContract[contractId]
    const billableLeadCount = sumBy(contractData, 'billableLeadCount')
    const totalCostInCents = sumBy(contractData, 'totalCostInCents')

    return {
      contractId: contractId,
      contract: contractData[0].contract,
      vendorName: contractData[0].vendorName,
      invoiceMonth: invoicePeriod.month() + 1,
      invoiceYear: invoicePeriod.year(),
      status: 'Draft',
      billableLeadCount: billableLeadCount,
      totalCostInCents: totalCostInCents,
      invoiceLineItems: contractData.map((row, index) => {
        return {
          id: row.id,
          billableLeadCount: row.billableLeadCount,
          subtotalInCents: row.totalCostInCents,
          clientCampaignName: row.clientCampaignName,
          publicId: row.publicId,
          position: index + 1,
        }
      })
    }
  })

  return invoices
}

interface DraftInvoicesProps {
  client: ClientRecord
  contractsResource: ContractsResource
  timezone: string
  existingInvoices: InvoiceFieldsFragment[]
  setRefreshData: React.Dispatch<SetStateAction<number>>
  permitSend: boolean
}

function DraftInvoices({ client, contractsResource, timezone, existingInvoices, setRefreshData, permitSend }: DraftInvoicesProps) {
  const { getRemoteId, remote, store } = useOrbit()
  const previousMonth = useMemo(() => (moment.tz(timezone).utc(true).startOf('month').subtract(1, 'month')), [timezone])
  // month() is an index and gives 0-11 as Jan-Dec, so we add one
  const previousMonthNumeric = previousMonth.month() + 1
  const previousMonthInvoices = useMemo(() => (existingInvoices.filter(invoice => invoice.month === previousMonthNumeric && invoice.year === previousMonth.year())), [existingInvoices, previousMonth, previousMonthNumeric])

  const clientCampaignsResource = useMemo(() => {
    return new ClientCampaignsResource({remote, contractOrClient: client, year: previousMonth.year(), month: previousMonthNumeric, programGroups: true})
  }, [remote, client, previousMonth, previousMonthNumeric])

  const [data, setData] = useState<RowData[] | null>(null)

  useEffect(() => {
    const invoicedContractIds = [...new Set(previousMonthInvoices.map(i => i.contract.id))]
    Promise.all([clientCampaignsResource.promise, contractsResource.promise]).then(([clientCampaigns, contracts]) => {
      const data = clientCampaigns
        .filter(clientCampaign => getIn(clientCampaign, 'attributes.status') === 'active')
        .map(clientCampaign => {
          const clientCampaignId = getRemoteId(clientCampaign) as string
          const billableLeadCount = getIn(clientCampaign, 'attributes.billableLeadCount')
          const contract = contracts.find((contract: ContractRecord) => getIn(clientCampaign, 'relationships.contract.data.id') === getIn(contract, 'id'))
          const vendorName = getIn(store.cache.query(q => q.findRelatedRecord(contract, 'vendor')), 'attributes.name')
          const publicId = getIn(clientCampaign, 'attributes.publicId')

          return {
            id: clientCampaignId,
            publicId: publicId,
            vendorName: vendorName,
            clientCampaignName: clientCampaign.attributes.name as string,
            status: 'Draft',
            billableLeadCount: billableLeadCount,
            totalCostInCents: getIn(clientCampaign, 'attributes.totalCostInCents'),
            contract: contract,
            contractId: getRemoteId(contract) as string,
          } as const
        })
        .filter(row => row.billableLeadCount > 0)
        .filter(row => !invoicedContractIds.includes(row.contractId))
        .sort((a, b) => a.vendorName.localeCompare(b.vendorName))
        .sort((a, b) => a.clientCampaignName.localeCompare(b.clientCampaignName))

      setData(data)
    })
  }, [clientCampaignsResource, setData, contractsResource.promise, previousMonthInvoices, store, getRemoteId])

  const initialState = { invoices: [], currentIndex: 0, selectedContractId: undefined, open: false }
  const [state, dispatch] = useReducer(dialogStateReducer, initialState);

  useEffect(() => {
    if(!data) {
      return
    }

    const invoices = invoicesFromData(data, previousMonth)
    dispatch({ type: 'UPDATE_INVOICES', invoices: invoices })

    // Send CLOSE_DIALOG to maintain correct open state
    // (it will not display already since the dialog depends on currentInvoice)
    if (invoices.length === 0 ) {
      dispatch({ type: 'CLOSE_DIALOG'})
    }
  }, [data, previousMonth])

  const currentInvoice: Invoice | undefined = isOpenSingleDialogState(state) ? state.invoices.find(i => i.contractId === state.selectedContractId ) : isOpenMultipleDialogState(state) ? state.invoices[state.currentIndex] : undefined
  const showPrevAndNext = isOpenMultipleDialogState(state) && state.invoices.length > 1

  const { setBookends } = useBookends()
  useEffect(() => {
    setBookends({
      footer: {
        buttons: [
          ...(permitSend ? [{
            type: 'primary',
            label: 'Lock or Send All Draft Invoices',
            disabled: state.invoices.length === 0,
            onClick: () => dispatch({ type: 'SEND_ALL_INVOICES' }),
          } as const] : []),
        ]
      }
    })
  }, [permitSend, setBookends, state.invoices.length])

  function Send({ rowNode }) { return (rowNode.type === 'group' && permitSend) ? <SendButton clickHandler={(_e: unknown, rowData: GridBasicGroupNode) => dispatch({ type: 'SEND_INVOICE', allocationId: rowData.children[0].toString() })} rowNode={rowNode} label='Lock or Send Invoice' Icon={LockSendIcon} /> : null }

  return (
    <>
      <StyledCard
        title={`${previousMonth.format("MMMM")} Draft Invoices`}
        note='Billable lead count and CPL Drafts ready to be sent to vendors for invoice request.'
        controls={[
            ...(permitSend ? [{icon: <LockSendIcon/>, onClick: () => { dispatch({ type: 'SEND_ALL_INVOICES' }) }, label: 'Lock or Send All Draft Invoices', disabled: state.invoices.length === 0}] : []),
          ]
        }
        isSubsection
        hasStickyHeader
        role="group"
      >
        <InvoiceTable variant='draft' data={data} groupingField='vendorName' leafField='clientCampaignName' customColumns={[CLIENT_CAMPAIGN_COLUMN]} controlRenderCell={Send} />
      </StyledCard>

      {state.open && currentInvoice &&
        <SendDialog
          invoice={currentInvoice}
          invoiceMoment={previousMonth}
          onComplete={() => setRefreshData(value => value + 1)}
          handleClose={() => dispatch({ type: 'CLOSE_DIALOG'})}
          onClickPrev={showPrevAndNext ? () => dispatch({ type: 'DECREMENT_INDEX'}) : undefined}
          onClickNext={showPrevAndNext ? () => dispatch({ type: 'INCREMENT_INDEX'}) : undefined}
        />
      }
    </>
  )
}

type InvoiceStatus = 'Sent' | 'Draft'

type InvoiceData = {
  id: string
  invoiceMoment: Moment
  invoicedPeriod: string
  vendorName: string
  status: InvoiceStatus
  billableLeadCount: number
  totalCostInCents: number
  invoiceLineItems: InvoiceLineItem[]
  sentAt: Moment
  contract: ContractRecord
}

type RowData = {
  id: string
  publicId: string
  vendorName: string
  clientCampaignName: string
  status: InvoiceStatus
  billableLeadCount: number
  totalCostInCents: number
  contract: ContractRecord
  contractId: string
}

interface InvoiceHistoryProps {
  invoices: InvoiceFieldsFragment[]
  contractsResource: ContractsResource
  timezone: string
  permitSend: boolean
}

function InvoiceHistory({ invoices, contractsResource, timezone, permitSend } : InvoiceHistoryProps) {
  const { getRemoteId } = useOrbit()
  const contracts = contractsResource.get()

  const data : InvoiceData[] = useMemo(() => {
    return invoices.map(invoice => {
      const billableLeadCount = sumBy(invoice.invoiceLineItems, 'billableLeadCount')
      const totalCostInCents = sumBy(invoice.invoiceLineItems, 'subtotalInCents')
      // Remember, moment expects months by their index
      const invoiceMoment = moment([invoice.year, invoice.month - 1])

      return {
        id: invoice.id,
        invoiceMoment: invoiceMoment,
        invoicedPeriod: invoiceMoment.format('MMMM YYYY'),
        vendorName: invoice.contract.vendor.name,
        contract: contracts.find((c: ContractRecord) => getRemoteId(c) === invoice.contract.id),
        status: 'Sent',
        billableLeadCount: billableLeadCount,
        totalCostInCents: totalCostInCents,
        invoiceLineItems: invoice.invoiceLineItems.map((item) => {
          return {
            publicId: item.clientCampaignExportableId.replace('CAMP-',''),
            ...item,
          }
        }),
        sentAt: invoice.sentAt,
      } as const
    }).sort((a, b) => b.invoiceMoment.diff(a.invoiceMoment))
  }, [contracts, getRemoteId, invoices])

  const [invoiceForModal, setInvoiceForModal] = useState<InvoiceData|null>(null);
  const handleOpen = (_e: unknown, rowData: RowData) => {
    setInvoiceForModal(data.find(row => row.id === rowData?.id) ?? null);
  };
  const handleClose = () => setInvoiceForModal(null)
  const [submitting, setSubmitting] = useState(false)

  const vendorName = invoiceForModal?.vendorName

  // TODO: remove duplication between Send and Resend
  const { addNotification } = useNotifications()
  const [gqlResend] = useMutation(RESEND_INVOICE_MUTATION)
  const handleSend = useCallback(() => {
    if(!invoiceForModal) {
      return
    }

    setSubmitting(true)

    const mutation = {
      id: invoiceForModal.id,
      sendEmail: true,
    }
    gqlResend({ variables: mutation }).then(() => {
      addNotification({variant: 'notice', message: `Invoice Request resent to ${vendorName}`})
      handleClose()
    }).catch(_error => {
      addNotification({variant: 'alert', message: "Trouble sending Invoice Request. Please try again later."})
    }).finally(() => {
      setSubmitting(false)
    })
  }, [addNotification, gqlResend, invoiceForModal, vendorName])

  function Resend({ rowNode }) { return (rowNode.type !== 'group' && permitSend) ? <SendButton clickHandler={handleOpen} rowNode={rowNode} label='Resend Invoice' Icon={SendIcon} /> : null }

  return (
    <>
      <StyledCard
        title='Invoice History'
        note='Billable lead count and CPLs are frozen from adjustment once invoice status is Sent.'
        isSubsection
        hasStickyHeader
        role="group"
      >
        <InvoiceTable variant='history' data={data} groupingField='invoicedPeriod' leafField='vendorName' customColumns={[INVOICED_PERIOD_COLUMN]} controlRenderCell={Resend} onLeafRowClick={handleOpen} />
      </StyledCard>

      {!!invoiceForModal &&
        <StandardDialog
          title={`${invoiceForModal.invoicedPeriod} Invoice Request`}
          description={<>Invoice request for <b>{invoiceForModal.invoicedPeriod}</b> sent to <b>{vendorName}</b> on <b>{moment.tz(invoiceForModal.sentAt, timezone).format('M/D/YY')}</b> at <b>{moment.tz(invoiceForModal.sentAt, timezone).format('h:mmA')}</b>.</>}
          onCancel={handleClose}
          onConfirm={handleSend}
          confirmText='Resend'
          maxWidth='840px'
          loading={submitting}
          disabled={!permitSend}
        >
          <InvoiceRequestContent invoice={invoiceForModal} invoiceMoment={moment.tz(invoiceForModal.invoicedPeriod, 'UTC')} />
        </StandardDialog>
      }
    </>
  )
}

interface InvoicePageProps {
  clientId: string
}

function InvoicePage({ clientId }: InvoicePageProps) {
  const { store, remote } = useOrbit()
  const { timezone } = useTimezone()
  const [refreshData, setRefreshData] = useState(0)
  const { getClientRole } = useCurrentUser()

  const client = store.cache.query<ClientRecord>(q => q.findRecord({type: "client", id: clientId}))
  const hasPermitEditRole = !!getIn(getClientRole(clientId), 'attributes.permitEdit')
  const permitSend = hasPermitEditRole

  const { data: { invoices } , refetch } = useSuspenseQuery(GET_INVOICES_QUERY, {
    variables: { clientId: clientId }
  })
  const clientInvoices = invoices.filter(invoice => invoice.contract.client.id === clientId)

  const contractsResource = useMemo(() => {
    return new ContractsResource({remote, clientId})
  }, [remote, clientId])

  useEffect(() => {
    refetch()
  }, [refetch, refreshData])

  return (
    <PageSection>
      <SectionTitle title='Invoices' />
      <DraftInvoices client={client} contractsResource={contractsResource} timezone={timezone} existingInvoices={clientInvoices} setRefreshData={setRefreshData} permitSend={permitSend} />
      <Suspenseful component={InvoiceHistory} contractsResource={contractsResource} invoices={clientInvoices} timezone={timezone} permitSend={permitSend} />
    </PageSection>
  )
}

export default function InvoicePageWrapper({ clientId }) {
  return (
    <Suspenseful component={InvoicePage} clientId={clientId} />
  )
}
